// Seleção do próximo time a partir da fila — fonte única usada pelo botão do app
// (GenerateBalancedTeamBtn, com embaralhamento) e pela sugestão do controle remoto
// (suggestNextTeam, determinística).
import { getPlayerPoints, sumPoints } from './points';
import { findBestCombination } from './combinations';

const identity = (array) => array;
const isFemale = (p) => p.gender === 'F';
const isSetter = (p) => p.playerType === 'Setter';

// Mesma ordem da fila exibida: maior espera primeiro; empate -> "Check-in agora" junto.
const queueOrder = (a, b) => (b.waitingFor - a.waitingFor)
  || ((b.checkedInNow ? 1 : 0) - (a.checkedInNow ? 1 : 0));

const groupByWaiting = (sortedPlayers) => sortedPlayers.reduce((acc, player) => {
  if (acc.length === 0 || acc[acc.length - 1][0].waitingFor !== player.waitingFor) {
    acc.push([player]);
  } else {
    acc[acc.length - 1].push(player);
  }
  return acc;
}, []);

// Pega até `slots` jogadores na ordem dada, sem passar do limite de mulheres.
const pickInOrder = (candidates, slots, maxFemales) => candidates.reduce((acc, p) => {
  if (acc.added.length >= slots) return acc;
  if (isFemale(p) && acc.femalesLeft <= 0) return acc;
  return {
    added: [...acc.added, p],
    femalesLeft: isFemale(p) ? acc.femalesLeft - 1 : acc.femalesLeft,
  };
}, { added: [], femalesLeft: maxFemales }).added;

// Retorna { targetTeamId, selectedPlayers, priorityPlayers, playersMissing,
//           currentTargetTeam, opposingTeam, playersWaiting }.
// priorityPlayers = quem tinha direito à vaga pela fila e foi passado para trás por quem
// furou a fila (mulher prioritária / levantador) — recebem "Próxima" (+10).
export function selectNextTeam({
  players,
  playersPerTeam,
  targetTeamId: forcedTargetTeamId,
  genderBalanceMode = false,
  genderRestrictedMode: restrictedSetting = false,
  femalesPerTeam = 1,
  setterRestrictedMode = false,
  autoBalanceMode = true,
  shuffle = identity,
}) {
  // O modo restrito só vale com o equilíbrio de gênero ligado (a chave some da tela quando ele
  // é desligado, mas o valor continua salvo).
  const genderRestrictedMode = genderBalanceMode && restrictedSetting;
  const teamAPlayers = players.filter((p) => p.teamId === 1);
  const teamBPlayers = players.filter((p) => p.teamId === 2);
  const targetTeamId = forcedTargetTeamId || (teamAPlayers.length <= teamBPlayers.length ? 1 : 2);
  const currentTargetTeam = targetTeamId === 1 ? teamAPlayers : teamBPlayers;
  const opposingTeam = targetTeamId === 1 ? teamBPlayers : teamAPlayers;
  const opposingTeamScore = sumPoints(opposingTeam);
  const playersMissing = Math.max(0, playersPerTeam - currentTargetTeam.length);
  const playersWaiting = players.filter((p) => p.teamId === 0 && p.checkedIn).sort(queueOrder);

  const base = {
    targetTeamId, playersMissing, currentTargetTeam, opposingTeam, playersWaiting,
  };
  if (playersMissing === 0) return { ...base, selectedPlayers: [], priorityPlayers: [] };

  const currentFemaleCount = currentTargetTeam.filter(isFemale).length;
  const femalesNeeded = genderBalanceMode ? Math.max(0, femalesPerTeam - currentFemaleCount) : 0;
  const initialTeamScore = sumPoints(currentTargetTeam);
  const needSetterForRestricted = setterRestrictedMode
    && opposingTeam.some(isSetter) && !currentTargetTeam.some(isSetter);

  // Embaralha dentro de cada grupo de espera (stayIn primeiro), sem furar a fila.
  const shuffleWithinGroups = (sorted) => groupByWaiting(sorted).flatMap((g) => [
    ...g.filter((p) => p.stayIn),
    ...shuffle(g.filter((p) => !p.stayIn)),
  ]);

  // Mulheres prioritárias (furam a fila). No modo restrito respeita o limite por time.
  // Se precisamos de levantador (modo restrito) e há levantadora, ela vem primeiro.
  let priorityFemales = [];
  const maxFemalesAllowed = genderRestrictedMode
    ? Math.max(0, femalesPerTeam - currentFemaleCount)
    : femalesNeeded;
  const femaleSetterForRestricted = needSetterForRestricted
    ? playersWaiting.find((p) => isSetter(p) && isFemale(p))
    : null;

  if (genderBalanceMode && femalesNeeded > 0 && maxFemalesAllowed > 0) {
    const closeness = (p) => Math.abs((initialTeamScore + getPlayerPoints(p)) - opposingTeamScore);
    const femaleOrder = (a, b) => {
      if (b.waitingFor !== a.waitingFor) return b.waitingFor - a.waitingFor;
      if (a.stayIn && !b.stayIn) return -1;
      if (!a.stayIn && b.stayIn) return 1;
      if (autoBalanceMode) return closeness(a) - closeness(b);
      return 0;
    };
    const femaleSetter = femaleSetterForRestricted;
    const otherFemales = playersWaiting
      .filter(isFemale)
      .filter((p) => !femaleSetter || p.playerId !== femaleSetter.playerId);
    let ordered = [...otherFemales].sort(femaleOrder);
    if (!autoBalanceMode) ordered = shuffleWithinGroups(ordered);
    const finalFemales = femaleSetter ? [femaleSetter, ...ordered] : ordered;
    priorityFemales = finalFemales.slice(0, Math.min(femalesNeeded, maxFemalesAllowed));
  }

  const initialSelected = priorityFemales.slice(0, playersMissing);
  const initialIds = new Set(initialSelected.map((p) => p.playerId));
  const slotsForNonPriority = playersMissing - initialSelected.length;
  const initialFemalesAllowed = genderRestrictedMode
    ? Math.max(0, femalesPerTeam - currentFemaleCount - initialSelected.filter(isFemale).length)
    : Infinity;

  // Seleção por grupo de espera; stayIn tem prioridade DENTRO do grupo.
  // O limite de mulheres é aplicado ANTES de escolher, para não sobrar vaga para quem espera menos.
  const selectionState = groupByWaiting(playersWaiting).reduce((state, group) => {
    if (state.selected.length >= slotsForNonPriority) return state;

    const availableInGroup = group.filter((p) => !initialIds.has(p.playerId));
    const slotsLeft = slotsForNonPriority - state.selected.length;
    const stayInAdded = pickInOrder(
      availableInGroup.filter((p) => p.stayIn),
      slotsLeft,
      state.femalesAllowed,
    );
    const femalesLeft = state.femalesAllowed - stayInAdded.filter(isFemale).length;
    const slotsAfterStayIn = slotsLeft - stayInAdded.length;
    const nonStayIn = availableInGroup.filter((p) => !p.stayIn);

    let nonStayInAdded;
    if (autoBalanceMode) {
      const currentBaseScore = initialTeamScore
        + sumPoints(initialSelected)
        + sumPoints(state.selected)
        + sumPoints(stayInAdded);
      nonStayInAdded = findBestCombination(
        nonStayIn,
        slotsAfterStayIn,
        currentBaseScore,
        opposingTeamScore,
        femalesLeft,
      );
    } else {
      nonStayInAdded = pickInOrder(shuffle(nonStayIn), slotsAfterStayIn, femalesLeft);
    }

    return {
      selected: [...state.selected, ...stayInAdded, ...nonStayInAdded],
      femalesAllowed: femalesLeft - nonStayInAdded.filter(isFemale).length,
    };
  }, { selected: [], femalesAllowed: initialFemalesAllowed });

  let selectedPlayers = [...initialSelected, ...selectionState.selected];

  // Se ainda faltam jogadores (caso raro), completa pela ordem da fila respeitando o limite.
  if (selectedPlayers.length < playersMissing) {
    const chosenIds = new Set(selectedPlayers.map((p) => p.playerId));
    const femalesStillAllowed = genderRestrictedMode
      ? Math.max(0, femalesPerTeam - currentFemaleCount - selectedPlayers.filter(isFemale).length)
      : Infinity;
    const additional = pickInOrder(
      playersWaiting.filter((p) => !chosenIds.has(p.playerId)),
      playersMissing - selectedPlayers.length,
      femalesStillAllowed,
    );
    selectedPlayers = [...selectedPlayers, ...additional];
  }

  if (selectedPlayers.length > playersMissing) {
    selectedPlayers = selectedPlayers.slice(0, playersMissing);
  }

  // Modo levantador restrito: garante um levantador no time que entra.
  let setterDisplacedPlayer = null;
  if (needSetterForRestricted && !selectedPlayers.some(isSetter)) {
    const waitingSetters = playersWaiting
      .filter((p) => isSetter(p) && !selectedPlayers.includes(p));
    if (waitingSetters.length > 0) {
      let setterToAdd;
      if (autoBalanceMode) {
        const baseScore = initialTeamScore + sumPoints(selectedPlayers);
        const closeness = (p) => Math.abs((baseScore + getPlayerPoints(p)) - opposingTeamScore);
        // Respeita a fila: maior espera primeiro; mesma espera -> o que mais equilibra.
        [setterToAdd] = [...waitingSetters]
          .sort((a, b) => (b.waitingFor - a.waitingFor) || (closeness(a) - closeness(b)));
      } else {
        [setterToAdd] = shuffleWithinGroups(waitingSetters);
      }
      const nonSettersSelected = selectedPlayers.filter((p) => !isSetter(p));

      // Quem sai para dar lugar ao levantador (considera o gênero no modo restrito).
      let nonSettersToKeep;
      const removeLast = () => {
        nonSettersToKeep = nonSettersSelected.slice(0, playersMissing - 1);
        setterDisplacedPlayer = nonSettersSelected[playersMissing - 1] || null;
      };
      const removeSpecific = (candidates) => {
        if (candidates.length === 0) { removeLast(); return; }
        setterDisplacedPlayer = candidates[candidates.length - 1];
        nonSettersToKeep = nonSettersSelected
          .filter((p) => p.playerId !== setterDisplacedPlayer.playerId)
          .slice(0, playersMissing - 1);
      };

      if (genderBalanceMode && genderRestrictedMode) {
        const femalesInSelected = nonSettersSelected.filter(isFemale);
        const totalFemalesWithSetter = currentFemaleCount + femalesInSelected.length
          + (isFemale(setterToAdd) ? 1 : 0);
        if (isFemale(setterToAdd) && totalFemalesWithSetter > femalesPerTeam) {
          removeSpecific(femalesInSelected);
        } else if (!isFemale(setterToAdd)
          && femalesInSelected.length < (femalesPerTeam - currentFemaleCount)) {
          removeSpecific(nonSettersSelected.filter((p) => !isFemale(p)));
        } else {
          removeLast();
        }
      } else {
        removeLast();
      }

      selectedPlayers = [setterToAdd, ...nonSettersToKeep];
    }
  }

  // Compensação: só quem tinha direito à vaga pela fila e perdeu para alguém que furou a fila.
  // (Dentro do mesmo grupo de espera ninguém tem direito individual, então trocas
  // entre empatados não geram prioridade.)
  let priorityPlayers = [];
  const pureQueueTop = playersWaiting.slice(0, playersMissing);
  if (pureQueueTop.length > 0) {
    const boundaryWait = pureQueueTop[pureQueueTop.length - 1].waitingFor;
    const selectedIds = new Set(selectedPlayers.map((p) => p.playerId));
    const entitled = playersWaiting.filter((p) => p.waitingFor >= boundaryWait);
    const entitledSelected = entitled.filter((p) => selectedIds.has(p.playerId)).length;
    const jumpers = selectedPlayers.length - entitledSelected;
    const lostSlots = Math.min(pureQueueTop.length - entitledSelected, jumpers);
    if (lostSlots > 0) {
      const passedOver = entitled.filter((p) => !selectedIds.has(p.playerId));
      const ordered = setterDisplacedPlayer && passedOver.includes(setterDisplacedPlayer)
        ? [setterDisplacedPlayer, ...passedOver.filter((p) => p !== setterDisplacedPlayer)]
        : passedOver;
      // Quem já tem "Próxima" continua no topo; não acumula +10.
      priorityPlayers = ordered.slice(0, lostSlots).filter((p) => p.waitingFor < 10);
    }
  }

  return { ...base, selectedPlayers, priorityPlayers };
}

// Versão determinística para a sugestão (controle remoto / modal "Próximos jogadores").
// Retorna { targetTeamId, playerIds, priorityIds }.
export function suggestNextTeam(config) {
  const { targetTeamId, selectedPlayers, priorityPlayers } = selectNextTeam(config);
  return {
    targetTeamId,
    playerIds: selectedPlayers.map((p) => p.playerId),
    priorityIds: priorityPlayers.map((p) => p.playerId),
  };
}
